import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EnvToggle } from "../OrgUsageAnalytics/components/EnvToggle";
import { useEnv } from "../OrgUsageAnalytics/useEnv";
import OrgPicker from "../OrgOverview/OrgPicker";
import { getLoaPermissions, saveLoaPermissions } from "./api";
import "../OrgUsageAnalytics/OrgUsageAnalytics.css";
import "../ReferralEligibility/ReferralEligibility.css";
import "./LoaPermissions.css";

/**
 * Plain labels for the switches the backend accepts (LOA_PERMISSION_KEYS).
 * A key the backend returns that is missing here still renders, by its key,
 * so a new switch is never hidden.
 */
export const PERMISSION_LABELS = {
  canViewDocuments: { label: "View documents", detail: "Open documents on the loan." },
  canDownloadDocuments: { label: "Download documents", detail: "Download and export documents." },
  canUploadDocuments: { label: "Upload documents", detail: "Add documents to the loan." },
  canManageDocuments: {
    label: "Manage documents",
    detail:
      "Change a document's type, move it to another folder or borrower, re-process it, run fraud checks and request documents.",
  },
  canDeleteDocuments: { label: "Delete documents", detail: "Remove documents from the loan." },
  canViewProcessingChecklist: { label: "See checklist results", detail: "Read task results." },
  canManageProcessingChecklist: {
    label: "Work the checklist",
    detail: "Run tasks, edit results, set task status and handle borrower approval conditions.",
  },
  canTrainAssistants: { label: "Training instructions", detail: "Create and edit training instructions." },
  canEditNotes: { label: "Notes", detail: "Add and edit notes on the loan." },
  canPrepareClosingWorksheet: {
    label: "Prepare closing worksheet",
    detail: "Load and preview it. Pushing it to the LOS needs Edit loan.",
  },
  canEditLoan: {
    label: "Edit loan",
    detail:
      "Loan details, stage, borrowers, contacts and who can access the loan, push to the LOS, delete or archive the loan.",
  },
};

function formatTimestamp(value) {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
}

// fetchWithFallback folds the backend body into its error; surface the backend reason when present.
function errorMessage(error) {
  const reason = String(error?.message || "").match(/"reason":"([^"]+)"/)?.[1];

  return reason || error?.message || "Something went wrong. Please try again.";
}

/**
 * The editable shape. A switch is on unless it was saved as off, because an
 * assistant starts with loan officer access and the settings only take away.
 */
function toDraft(settings, permissionKeys) {
  const permissions = {};
  for (const key of permissionKeys) {
    permissions[key] = settings?.permissions?.[key] !== false;
  }

  return {
    enabled: settings?.enabled === true,
    enforceNewChecks: settings?.enforceNewChecks === true,
    maxLoasPerSeat: Number.isInteger(settings?.maxLoasPerSeat) ? settings.maxLoasPerSeat : null,
    permissions,
  };
}

function SeatLimitSummary({ seatUsage, defaultPerSeat }) {
  if (!seatUsage) {
    return null;
  }

  if (!seatUsage.limited) {
    return (
      <p className="ref-muted loa-detail">
        Not limited: this organization existed before the limit started or is not on a self-serve Growth plan.{" "}
        {seatUsage.currentLoas} {seatUsage.currentLoas === 1 ? "assistant" : "assistants"} today.
      </p>
    );
  }

  return (
    <p className="ref-muted loa-detail">
      {seatUsage.paidSeats} paid {seatUsage.paidSeats === 1 ? "seat" : "seats"} × {seatUsage.maxLoasPerSeat} ={" "}
      <strong>{seatUsage.maxLoas}</strong> assistants allowed, {seatUsage.currentLoas} in use (default is{" "}
      {defaultPerSeat} per seat).
    </p>
  );
}

const LoaPermissionsPage = ({ onReady }) => {
  const [env, setEnv] = useEnv();
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    onReady?.();
  }, [onReady]);

  const organizationId = selectedOrg?.id || null;

  const load = useCallback(async () => {
    if (!organizationId) {
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await getLoaPermissions(env, organizationId);
      setData(response);
      setDraft(toDraft(response.settings, response.permissionKeys || []));
    } catch (loadError) {
      setData(null);
      setDraft(null);
      setError(errorMessage(loadError));
    } finally {
      setIsLoading(false);
    }
  }, [env, organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  const permissionKeys = useMemo(() => data?.permissionKeys || [], [data]);
  const saved = useMemo(
    () => (data ? toDraft(data.settings, permissionKeys) : null),
    [data, permissionKeys],
  );
  const isDirty = Boolean(draft && saved && JSON.stringify(draft) !== JSON.stringify(saved));
  const defaultPerSeat = data?.defaultMaxLoasPerSeat ?? 2;

  // Read input values before calling setDraft: the updater runs later, when
  // React has already reset the controlled input to its old value.
  const setDraftField = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const setPermission = (key, value) => {
    setDraft((current) => ({
      ...current,
      permissions: { ...current.permissions, [key]: value },
    }));
  };

  const resetToLoanOfficer = () => {
    setDraft((current) => ({
      ...current,
      permissions: Object.fromEntries(permissionKeys.map((key) => [key, true])),
    }));
  };

  const setMaxLoasPerSeat = (rawValue) => {
    const trimmed = String(rawValue).trim();
    const parsed = Number(trimmed);
    setDraftField("maxLoasPerSeat", trimmed === "" || !Number.isInteger(parsed) ? null : parsed);
  };

  const save = async () => {
    if (!draft || isSaving) {
      return;
    }

    setIsSaving(true);
    try {
      const response = await saveLoaPermissions(env, organizationId, draft);
      setDraft(toDraft(response.settings, permissionKeys));
      toast.success("LOA settings saved");
      // Reload so the seat usage reflects a changed limit.
      load();
    } catch (saveError) {
      toast.error(errorMessage(saveError));
    } finally {
      setIsSaving(false);
    }
  };

  const updatedAt = formatTimestamp(data?.settings?.updatedAt);

  return (
    <div className="ref-page">
      <ToastContainer position="top-right" autoClose={3500} />

      <div className="ref-page-header">
        <div>
          <p className="ref-eyebrow">Access</p>
          <h1 className="ref-page-title">LOA Permissions</h1>
          <p className="ref-page-subtitle">
            Loan Officer Assistants have the same access as loan officers. Per organization, take actions away
            from them and set how many assistants a Growth organization may have.
          </p>
        </div>
        <div className="ref-header-controls">
          <OrgPicker
            env={env}
            selectedOrg={selectedOrg}
            onSelect={setSelectedOrg}
            onClear={selectedOrg ? () => setSelectedOrg(null) : undefined}
          />
          <EnvToggle env={env} onChange={setEnv} />
        </div>
      </div>

      {!organizationId ? <div className="ref-empty">Pick an organization to begin.</div> : null}
      {error ? (
        <div className="ref-error" role="alert">
          {error}
        </div>
      ) : null}
      {organizationId && isLoading ? <div className="ref-empty">Loading LOA settings…</div> : null}

      {organizationId && !isLoading && draft ? (
        <section className="ref-add-panel loa-panel">
          <div className="loa-panel-head">
            <div>
              <h2 className="ref-section-title">{selectedOrg?.name || organizationId}</h2>
              <p className="ref-section-subtitle">
                {updatedAt
                  ? `Last changed ${updatedAt} by ${data.settings.updatedBy || "unknown"}.`
                  : "Never changed: assistants here have the same access as loan officers."}
              </p>
            </div>
          </div>

          <div className="loa-limit">
            <label className="loa-limit-field">
              <span className="loa-switch-label">Assistants allowed per paid seat</span>
              <input
                className="ref-input loa-limit-input"
                type="number"
                min={0}
                step={1}
                placeholder={String(defaultPerSeat)}
                value={draft.maxLoasPerSeat ?? ""}
                aria-label="Assistants allowed per paid seat"
                onChange={(event) => setMaxLoasPerSeat(event.target.value)}
              />
              <span className="ref-muted loa-detail">
                Leave empty for the default ({defaultPerSeat}). Applies to Growth organizations created after the
                limit started.
              </span>
            </label>
            <SeatLimitSummary seatUsage={data.seatUsage} defaultPerSeat={defaultPerSeat} />
          </div>

          <label className="loa-toggle">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(event) => setDraftField("enabled", event.target.checked)}
            />
            <span>
              <strong>Restrict assistants in this organization</strong>
              <span className="ref-muted loa-detail">
                Off means assistants have the same access as loan officers and the switches below are ignored.
              </span>
            </span>
          </label>

          <div className={`loa-switches${draft.enabled ? "" : " loa-switches-off"}`}>
            <div className="loa-switches-head">
              <span className="ref-muted">Untick what assistants should not be able to do.</span>
              <button
                type="button"
                className="ref-btn ref-btn-ghost ref-btn-sm"
                disabled={!draft.enabled}
                onClick={resetToLoanOfficer}
              >
                Same as loan officer
              </button>
            </div>
            {permissionKeys.map((key) => {
              const meta = PERMISSION_LABELS[key] || { label: key, detail: "" };

              return (
                <label className="loa-switch" key={key}>
                  <input
                    type="checkbox"
                    checked={draft.permissions[key] === true}
                    disabled={!draft.enabled}
                    onChange={(event) => setPermission(key, event.target.checked)}
                  />
                  <span>
                    <span className="loa-switch-label">{meta.label}</span>
                    {meta.detail ? <span className="ref-muted loa-detail">{meta.detail}</span> : null}
                  </span>
                </label>
              );
            })}
          </div>

          <label className="loa-toggle loa-enforce">
            <input
              type="checkbox"
              checked={draft.enforceNewChecks}
              disabled={!draft.enabled}
              onChange={(event) => setDraftField("enforceNewChecks", event.target.checked)}
            />
            <span>
              <strong>Block actions that are switched off</strong>
              <span className="ref-muted loa-detail">
                Some actions were never checked before. While this is off, those actions are only logged
                ("loa-permission-would-block"), not blocked. Check the logs before turning it on.
              </span>
            </span>
          </label>

          <div className="loa-actions">
            <button
              type="button"
              className="ref-btn ref-btn-ghost"
              disabled={!isDirty || isSaving}
              onClick={() => setDraft(saved)}
            >
              Discard changes
            </button>
            <button
              type="button"
              className="ref-btn ref-btn-primary"
              disabled={!isDirty || isSaving}
              onClick={save}
            >
              {isSaving ? "Saving…" : "Save"}
            </button>
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default LoaPermissionsPage;
